import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { 
  Building2, LayoutDashboard, Users, DollarSign, FolderKanban,
  Sparkles, Settings, LogOut, Menu, X, ChevronDown,
  TrendingUp, FileText, Receipt, Target, Briefcase, AlertTriangle,
  BookOpen, Package, Calendar, Bell, UserCircle, MessageCircle, Share2, Calculator, Shield,
  Wallet, CheckSquare, Activity, UserCog, FolderOpen, GraduationCap, ClipboardList, ListChecks, Plane,
  Search, Plus, Moon, Sun, PanelLeftClose, PanelLeft,
  MoreHorizontal, Gauge, ClipboardCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Full navigation structure with keys
const FULL_NAVIGATION = [
  {
    key: 'command_centre',
    name: 'Command Centre',
    href: '/command-centre',
    icon: Gauge,
    description: 'Enterprise health & KPIs'
  },
  { 
    key: 'dashboard',
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: LayoutDashboard,
    description: 'Executive overview'
  },
  {
    key: 'my_work',
    name: 'My Work',
    href: '/my-work',
    icon: ListChecks,
    description: 'Your tasks, projects & leads'
  },
  { 
    key: 'clients',
    name: 'Clients', 
    href: '/clients', 
    icon: Building2,
    description: 'Client management'
  },
  { 
    key: 'services',
    name: 'Services', 
    href: '/services', 
    icon: Package,
    description: 'Service offerings'
  },
  { 
    key: 'sales',
    name: 'Sales & CRM', 
    href: '/sales', 
    icon: TrendingUp,
    description: 'Leads, opportunities & quotations',
    children: [
      { name: 'Leads', href: '/sales?tab=leads', icon: Target },
      { name: 'Opportunities', href: '/sales?tab=opportunities', icon: Briefcase },
      { name: 'Quotations', href: '/sales?tab=quotations', icon: FileText }
    ]
  },
  { 
    key: 'finance',
    name: 'Finance', 
    href: '/finance', 
    icon: DollarSign,
    description: 'Invoices & payments',
    children: [
      { name: 'Workspace', href: '/accounting-workspace', icon: Calculator },
      { name: 'Bookkeeping', href: '/bookkeeping', icon: BookOpen },
      { name: 'Invoices', href: '/finance?tab=invoices', icon: Receipt },
      { name: 'Payments', href: '/finance?tab=payments', icon: DollarSign },
      { name: 'Accounting', href: '/accounting', icon: BookOpen },
      { name: 'Purchase Orders', href: '/purchase-orders', icon: Package }
    ]
  },
  { 
    key: 'projects',
    name: 'Projects', 
    href: '/projects', 
    icon: FolderKanban,
    description: 'Project management hub'
  },
  { 
    key: 'tasks',
    name: 'Tasks', 
    href: '/tasks', 
    icon: CheckSquare,
    description: 'Task management'
  },
  {
    key: 'approvals',
    name: 'Approvals',
    href: '/approvals',
    icon: ClipboardCheck,
    description: 'Executive approval centre'
  },
  { 
    key: 'grants',
    name: 'Grants', 
    href: '/grants', 
    icon: Wallet,
    description: 'Grant funding management'
  },
  { 
    key: 'travel',
    name: 'Travel', 
    href: '/travel', 
    icon: Plane,
    description: 'Travel & trip cost management'
  },
  { 
    key: 'risks',
    name: 'Risks', 
    href: '/risks', 
    icon: AlertTriangle,
    description: 'Risk register'
  },
  {
    key: 'compliance',
    name: 'Compliance',
    href: '/compliance',
    icon: Shield,
    description: 'SETA, QCTO, ISO, POPIA, OHS'
  },
  {
    key: 'bizdev',
    name: 'Business Development',
    href: '/bizdev',
    icon: Target,
    description: 'Tenders, proposals & forecast'
  },
  {
    key: 'learners',
    name: 'Learners',
    href: '/learners',
    icon: GraduationCap,
    description: 'Learner lifecycle'
  },
  {
    key: 'reports',
    name: 'Reports',
    href: '/reports',
    icon: FileText,
    description: 'PDF & Excel reports'
  },
  { 
    key: 'calendar',
    name: 'Calendar', 
    href: '/calendar', 
    icon: Calendar,
    description: 'Schedule & reminders'
  },
  { 
    key: 'social',
    name: 'Social & Messaging', 
    href: '/social-config', 
    icon: Share2,
    description: 'Social integrations'
  },
  { 
    key: 'ai',
    name: 'AI Assistant', 
    href: '/ai', 
    icon: Sparkles,
    description: 'AI-powered operations'
  },
  { 
    key: 'team',
    name: 'Team Management', 
    href: '/team', 
    icon: Users,
    description: 'Users, roles & permissions'
  },
  {
    key: 'permissions',
    name: 'Permissions',
    href: '/permissions',
    icon: Shield,
    description: 'Access control'
  },
  {
    key: 'audit',
    name: 'Audit Trail',
    href: '/audit-trail',
    icon: Activity,
    description: 'Activity logs & security audit'
  },
  {
    key: 'hr',
    name: 'Human Resources',
    href: '/hr',
    icon: UserCog,
    description: 'Employee management & leave'
  },
  {
    key: 'repository',
    name: 'Repository',
    href: '/repository',
    icon: FolderOpen,
    description: 'File management & sharing'
  },
  {
    key: 'student_billing',
    name: 'Student Billing',
    href: '/student-billing',
    icon: GraduationCap,
    description: 'Enrollment, fees & revenue'
  },
  {
    key: 'wfh_tracker',
    name: 'WFH Tracker',
    href: '/wfh-tracker',
    icon: ClipboardList,
    description: 'Import WFH spreadsheets & export employee reports'
  },
  { 
    key: 'settings',
    name: 'Settings', 
    href: '/settings', 
    icon: Settings,
    description: 'System configuration'
  }
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const [accessibleItems, setAccessibleItems] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  // Fetch user's accessible sidebar items
  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const res = await axios.get(`${API}/permissions/my-access`);
        setAccessibleItems(res.data.sidebar_items || []);
      } catch (error) {
        // Fallback to all items for super_admin or if API fails
        if (user?.role === 'super_admin') {
          setAccessibleItems(FULL_NAVIGATION.map(item => item.key));
        } else {
          // Default minimal access
          setAccessibleItems(['dashboard']);
        }
      } finally {
        setLoadingAccess(false);
      }
    };
    
    if (user) {
      fetchAccess();
    }
  }, [user]);

  // Filter navigation based on accessible items
  const navigation = FULL_NAVIGATION.filter(item => accessibleItems.includes(item.key));

  const toggleExpand = (name) => {
    setExpandedItems(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const [searchQ, setSearchQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [entityResults, setEntityResults] = useState([]);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setEntityResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, { params: { q } });
        setEntityResults(res.data.results || []);
      } catch (e) { setEntityResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => {
    let alive = true;
    const loadFocus = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/focus`);
        if (alive) setFocus(res.data);
      } catch (e) { /* silent */ }
    };
    loadFocus();
    const id = setInterval(loadFocus, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      localStorage.setItem('sidebar-collapsed', c ? '0' : '1');
      return !c;
    });
  };

  const flattenNav = navigation.flatMap(i => [i, ...(i.children || [])]);
  const searchResults = searchQ.trim()
    ? flattenNav.filter(i => i.name.toLowerCase().includes(searchQ.toLowerCase())).slice(0, 7)
    : [];

  const isActive = (href) => {
    const basePath = href.split('?')[0];
    return location.pathname === basePath;
  };

  const NavItem = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    const expanded = expandedItems.includes(item.name);
    const hideLbl = collapsed ? 'lg:hidden' : '';

    if (item.children) {
      return (
        <div className="space-y-1">
          <button
            onClick={() => { if (collapsed) toggleCollapsed(); toggleExpand(item.name); }}
            className={`w-full sidebar-item justify-between ${collapsed ? 'lg:justify-center' : ''} ${active ? 'sidebar-item-active' : ''}`}
            title={item.name}
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5" />
              <span className={hideLbl}>{item.name}</span>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''} ${hideLbl}`} />
          </button>
          {expanded && (
            <div className={`ml-4 pl-4 border-l space-y-1 ${hideLbl}`}>
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={() => mobile && setSidebarOpen(false)}
                    className="sidebar-item text-sm"
                  >
                    <ChildIcon className="h-4 w-4" />
                    <span>{child.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        title={item.name}
        className={`sidebar-item ${collapsed ? 'lg:justify-center' : ''} ${active ? 'sidebar-item-active' : ''}`}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-5 w-5" />
        <span className={hideLbl}>{item.name}</span>
      </Link>
    );
  };

  const sidebarWidth = collapsed ? 'lg:w-[76px]' : 'lg:w-64';
  const mainPad = collapsed ? 'lg:pl-[76px]' : 'lg:pl-64';

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside style={{ backgroundColor: '#0f172a' }} className={`
        fixed top-0 left-0 z-50 h-full w-64 ${sidebarWidth} sidebar-shell border-r transform transition-all duration-200 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`safe-top h-14 lg:h-16 flex items-center ${collapsed ? 'lg:justify-center' : 'justify-between'} px-3 lg:px-4 border-b shrink-0`}>
            <Link to="/dashboard" className="flex items-center gap-2 lg:gap-3 min-w-0">
              <img 
                src="/logo.png" 
                alt="IKWorx Logo" 
                className="h-8 w-8 lg:h-10 lg:w-10 object-contain shrink-0"
              />
              <div className={`flex flex-col min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
                <span className="text-xs lg:text-sm font-bold text-foreground leading-tight truncate">Innovative</span>
                <span className="text-[10px] lg:text-xs text-primary font-semibold leading-tight truncate">Knowledge Worx</span>
              </div>
            </Link>
            <button 
              className="lg:hidden p-2 rounded-md hover:bg-muted"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} mobile />
              ))}
            </nav>
          </ScrollArea>

          {/* Today's Focus */}
          {focus && (
            <div className={`mx-3 mb-2 p-3 rounded-xl bg-white/5 border border-white/10 ${collapsed ? 'lg:hidden' : ''}`} data-testid="today-focus-widget">
              <div className="flex items-center gap-2 text-slate-200 text-sm font-semibold">
                <Target className="h-4 w-4 text-blue-400" /> Today's Focus
              </div>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-slate-400">
                  {focus.due_today - focus.done_today > 0 ? `Close ${focus.due_today - focus.done_today} more tasks` : focus.due_today > 0 ? 'All caught up!' : 'Nothing due today'}
                </span>
                <span className="text-slate-300 font-mono">{focus.done_today}/{focus.due_today}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${focus.due_today ? Math.round(focus.done_today / focus.due_today * 100) : 0}%` }} />
              </div>
              <div className="flex items-center justify-between mt-3 text-xs">
                <span className="text-slate-400">Daily Goal</span>
                <span className="text-slate-300 font-mono">{focus.goal_pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${focus.goal_pct}%` }} />
              </div>
            </div>
          )}

          {/* User section */}
          <div className={`p-4 border-t ${collapsed ? 'lg:p-2' : ''}`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted transition-colors ${collapsed ? 'lg:justify-center' : ''}`}>
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary text-primary-foreground font-bold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`flex-1 text-left ${collapsed ? 'lg:hidden' : ''}`}>
                    <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ') || 'User'}</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground ${collapsed ? 'lg:hidden' : ''}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`${mainPad} transition-all duration-200`}>
        {/* Top bar */}
        <header className="safe-top sticky top-0 z-30 h-14 lg:h-16 glass-nav flex items-center gap-2 px-4 lg:px-6">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            className="hidden lg:flex p-2 rounded-xl hover:bg-muted text-muted-foreground"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>

          {/* Global search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={searchQ}
              onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              onFocus={() => searchQ && setSearchOpen(true)}
              placeholder="Search projects, clients, risks, documents…"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-muted/60 border border-transparent focus:border-ring focus:bg-card outline-none text-sm transition-colors duration-200"
              data-testid="global-search-input"
            />
            {searchOpen && (searchResults.length > 0 || entityResults.length > 0) && (
              <div className="absolute top-11 left-0 right-0 rounded-xl border bg-popover shadow-lg py-1 z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
                {searchResults.map(r => {
                  const RIcon = r.icon;
                  return (
                    <button
                      key={r.name + r.href}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
                      onMouseDown={() => { navigate(r.href); setSearchQ(''); setSearchOpen(false); }}
                    >
                      <RIcon className="h-4 w-4 text-muted-foreground" />
                      {r.name}
                      <span className="ml-auto text-[10px] uppercase text-muted-foreground">Module</span>
                    </button>
                  );
                })}
                {entityResults.map((r, i) => (
                  <button
                    key={`${r.type}-${r.id}-${i}`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
                    onMouseDown={() => { navigate(r.link); setSearchQ(''); setSearchOpen(false); }}
                    data-testid={`search-result-${r.type.toLowerCase()}-${i}`}
                  >
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0 w-20 text-center">{r.type}</span>
                    <span className="truncate">{r.title}</span>
                    {r.subtitle && <span className="ml-auto text-xs text-muted-foreground truncate max-w-[40%]">{r.subtitle}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-1.5">
            {/* Quick create */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="rounded-xl h-9 gap-1.5 shadow-sm" data-testid="quick-create-btn">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Create</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/tasks')}><CheckSquare className="h-4 w-4 mr-2" />New Task</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/sales')}><TrendingUp className="h-4 w-4 mr-2" />New Lead</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/projects-module')}><FolderKanban className="h-4 w-4 mr-2" />New Project</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/travel')}><Plane className="h-4 w-4 mr-2" />New Trip</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/announcements')}><Bell className="h-4 w-4 mr-2" />New Announcement</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <button
              className="p-2 rounded-xl hover:bg-muted text-muted-foreground relative"
              onClick={() => navigate('/announcements')}
              title="Notifications"
              data-testid="notifications-btn"
            >
              <Bell className="h-5 w-5" />
            </button>

            {/* Theme switcher */}
            <button
              className="p-2 rounded-xl hover:bg-muted text-muted-foreground"
              onClick={() => setDark(d => !d)}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              data-testid="theme-toggle-btn"
            >
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <span className="text-sm text-muted-foreground hidden xl:block pl-2">
              {user?.branch === 'main' ? 'Main Branch' : user?.branch}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar — only on small screens */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-background/95 backdrop-blur border-t flex justify-around items-stretch h-16"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-tabbar"
      >
        {[
          { to: '/dashboard', icon: LayoutDashboard, label: 'Home', testid: 'tab-home' },
          { to: '/my-work', icon: ListChecks, label: 'My Work', testid: 'tab-mywork' },
          { to: '/tasks', icon: CheckSquare, label: 'Tasks', testid: 'tab-tasks' },
          { to: '/repository', icon: FolderOpen, label: 'Files', testid: 'tab-files' },
        ].map((t) => {
          const active = location.pathname.startsWith(t.to);
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] transition-colors ${active ? 'text-primary font-semibold' : 'text-muted-foreground'}`}
            >
              <t.icon className={`h-5 w-5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
              {t.label}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          data-testid="tab-more"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] text-slate-500"
        >
          <MoreHorizontal className="h-5 w-5" />
          More
        </button>
      </nav>
    </div>
  );
};
